import StatCard from '@/components/dashboard/StatCard';
import { User } from '@/models/User';
import { UserGroupIcon } from '@heroicons/react/24/solid';

interface LastActiveUsersStatsProps {
  users: User[];
  isLoading: boolean;
  limit?: number;
}

const LatestActiveUsersStats = ({ users, isLoading, limit = 5 }: LastActiveUsersStatsProps) => {
  const recentUsers = [...users]
    .filter((u) => u.lastSignInAt)
    .sort((a, b) => new Date(b.lastSignInAt!).getTime() - new Date(a.lastSignInAt!).getTime())
    .slice(0, limit);

  return (
    <StatCard
      icon={<UserGroupIcon className="h-8 w-8 text-slate-400 mt-1" />}
      loading={false}
      value={
        <div className="overflow-x-auto">
          <table className="w-full min-w-full text-sm">
            <thead className="border-b text-gray-700 font-medium">
              <tr>
                <th className="py-2 text-left">User</th>
                <th className="py-2 text-right">Last Sign In</th>
              </tr>
            </thead>

            <tbody>
              {isLoading &&
                Array.from({ length: limit }).map((_, i) => (
                  <tr key={`loading-${i}`} className="border-b last:border-none">
                    <td className="py-3">
                      <div className="h-4 bg-gray-200 animate-pulse rounded w-32 mb-1" />
                      <div className="h-3 bg-gray-200 animate-pulse rounded w-24" />
                    </td>
                    <td className="py-3 text-right">
                      <div className="h-4 bg-gray-200 animate-pulse rounded w-20 mb-1 ml-auto" />
                      <div className="h-3 bg-gray-200 animate-pulse rounded w-14 ml-auto" />
                    </td>
                  </tr>
                ))}

              {!isLoading &&
                recentUsers.map((u) => (
                  <tr key={u.id} className="border-b last:border-none">
                    <td className="py-2">
                      <div className="text-gray-800">{u.fullName || u.username}</div>
                      <div className="text-gray-500 text-xs">{u.email}</div>
                    </td>

                    <td className="py-2 text-right text-gray-700">
                      {u.lastSignInAt && (
                        <>
                          <div>
                            {new Date(u.lastSignInAt).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(u.lastSignInAt).toLocaleTimeString('en-GB', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                ))}

              {!isLoading && recentUsers.length === 0 && (
                <tr>
                  <td colSpan={2} className="py-3 text-center text-gray-500 italic">
                    No recent activity
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      }
    />
  );
};

export default LatestActiveUsersStats;
